import { ColorPicker as AntdColorPicker } from 'antd';
import { Color } from 'antd/es/color-picker';
import { AggregationColor } from 'antd/es/color-picker/color';
import React, { useEffect, useState } from 'react';
import { HiHashtag } from 'react-icons/hi2';
import { Input } from '../Input';
import { withTheme } from '../ThemeProvider';
import { Container } from './styles';
import { ColorPickerProps } from './types';

const ColorPickerComponent: React.FC<ColorPickerProps> = ({
  disabledAlpha = true,
  defaultValue,
  inputProps = {
    placeholder: 'Color code',
  },
  ...rest
}: ColorPickerProps) => {
  const [colorRgb, setColorRgb] = useState<ReturnType<AggregationColor['toRgb']> | undefined>(
    () => {
      if (defaultValue) {
        const color = new AggregationColor(defaultValue);
        return color.toRgb();
      }
      return undefined;
    },
  );

  const [inputValue, setInputValue] = useState<string>(defaultValue?.toString() || '');

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const input = document.querySelector('.ant-color-picker-input-container');
      if (input) {
        (input as HTMLElement).style.display = 'none';
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  const updateColorRgb = (channel: 'r' | 'g' | 'b', value: string) => {
    if (!colorRgb) return;
    const newRgb = { ...colorRgb, [channel]: Number(value) };
    setColorRgb(newRgb);
    setInputValue(new AggregationColor(`rgb(${newRgb.r}, ${newRgb.g}, ${newRgb.b})`).toHexString());
  };

  const handleChangeComplete: ColorPickerProps['onChangeComplete'] = (value: Color) => {
    const stringValue = value.toHexString();
    setColorRgb(value.toRgb());
    setInputValue(stringValue);

    inputProps.onChange?.({
      target: { value: stringValue },
    } as React.ChangeEvent<HTMLInputElement>);

    rest.onChangeComplete?.(value);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value: string = event.target.value;
    setInputValue(value);
    inputProps.onChange?.(event);

    if (value) {
      const color = new AggregationColor(value);
      setColorRgb(color.toRgb());
    } else {
      setColorRgb(undefined);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setColorRgb(undefined);

    inputProps.onChange?.({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>);

    rest.onClear?.();
  };

  const renderRgbInput = (label: 'R' | 'G' | 'B', value?: number) => (
    <div key={label} className="flex flex-col items-center w-12">
      <div className="text-xs">{label}</div>
      <Input
        className="text-xs focus:border-1"
        value={value}
        onChange={e => updateColorRgb(label.toLowerCase() as 'r' | 'g' | 'b', e.target.value)}
      />
    </div>
  );

  const panelRender: NonNullable<ColorPickerProps['panelRender']> = (
    _,
    { components: { Picker } },
  ) => (
    <div>
      <Picker />
      <div className="mt-2 flex gap-1">
        <div className="flex flex-col items-center w-24">
          <div className="text-xs">HEX</div>
          <Input
            className="text-xs focus:border-1"
            value={inputValue}
            onChange={handleInputChange}
          />
        </div>
        {(['r', 'g', 'b'] as const).map(channel =>
          renderRgbInput(channel.toUpperCase() as 'R' | 'G' | 'B', colorRgb?.[channel]),
        )}
      </div>
    </div>
  );

  return (
    <Container>
      <Input
        placeholder={inputProps.placeholder}
        value={inputValue.replace('#', '')}
        prefix={
          <div className="prefix">
            <HiHashtag width={16} height={16} />
          </div>
        }
        onChange={handleInputChange}
        suffix={
          <AntdColorPicker
            disabledAlpha={disabledAlpha}
            defaultValue={defaultValue}
            value={inputValue}
            onChangeComplete={handleChangeComplete}
            panelRender={panelRender}
            disabledFormat
            destroyTooltipOnHide
            onClear={handleClear}
            {...rest}
          />
        }
        {...inputProps}
      />
    </Container>
  );
};

export const ColorPicker = withTheme(ColorPickerComponent);
